import React, { FC, useState } from "react";

import { useDeleteTask, useGetDag } from "api/api-hooks/airflow-hooks";
import { redirectToAirflowGraphUrl } from "api/ScheduledTasksApi";
import ActionButtons from "components/basic/action-buttons/ActionButtons";
import CollapsibleText from "components/basic/collapsible-text/CollapsibleText";
import DataTable from "components/basic/data-table/DataTable";
import { useHasPermissions } from "hooks/useHasPermissions";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { DataTableProps } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { useParams } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { AirflowDag, AirflowTaskDto } from "types/airflow";
import airflowLogo from "img/airflow-icon16.png";
import { TableColumn } from "../measurement-devices/utils/types";
import AirflowDagForm from "./components/AirflowDagForm";
import AirflowTaskForm from "./components/AirflowTaskForm";

const AirflowTasks: React.FC = () => {
  const { dagId } = useParams();
  const { data, isLoading } = useGetDag(dagId ?? "");
  const deleteTask = useDeleteTask();

  const canEdit = useHasPermissions("AIRFLOW_EDIT")

  if (!dagId) return null;

  const renderActions = (rowData: AirflowTaskDto) => {
    return canEdit ? (
      <ActionButtons
        urlOnLink={`${rowData.id.taskId}`}
        onClickDelete={() => deleteTask.mutate({ dagId, taskId: rowData.id.taskId })}
      />
    ) : (
      <ActionButtons urlOnLink={`${rowData.id.taskId}`} />
    );}

  const renderTaskTags = (streams: string[], prefix: "up" | "down") => (
    <div className="flex flex-column gap-1">
      {streams.length === 0 && <span>{`No ${prefix}stream tasks`}</span>}
      {streams.map(id => (
        <Tag
          key={id}
          value={id}
        />
      ))}
    </div>
  );

  const renderTaskId = (rowData: AirflowTaskDto) => <CollapsibleText text={rowData.id.taskId} />;

  const renderStatus = (rowData: AirflowTaskDto) => (
      <div>
        {rowData.isSkipped && <i
          className="pi pi-power-off text-red-600"
          title="Task is skipped (disabled)"
        />}
        {rowData.showStepWarning && <i
          className="pi pi-exclamation-triangle text-orange-600"
          title="Task is executing with more than 1 period steps!"
        />}
      </div>
    );

  const columns: TableColumn<AirflowTaskDto>[] = [
    {
      field: "id.taskId",
      header: "Task ID",
      columnKey: "taskId",
      body: renderTaskId,
    },
    {
      field: "title",
      header: "Title",
      columnKey: "title",
    },
    {
      field: "description",
      header: "Description",
      style: { maxWidth: "30rem", wordBreak: "break-word" },
      columnKey: "description",
    },
    {
      field: "upstreamTasks",
      header: "Invoked by",
      columnKey: "upstreamTasks",
      body: rowData => renderTaskTags(rowData.upstreamTasks, "up"),
    },
    {
      field: "downstreamTasks",
      header: "Invokes",
      columnKey: "downstreamTasks",
      body: rowData => renderTaskTags(rowData.downstreamTasks, "down"),
    },
    {
      header: "Status",
      columnKey: "status",
      body: renderStatus,
      style: { justifyItems: "center" },
    },
    {
      header: T("word.actions"),
      columnKey: "actions",
      body: renderActions,
      style: { justifyItems: "center" },
    },
  ];

  const tableConfig: DataTableProps<AirflowTaskDto[]> = {
    loading: isLoading,
    header: canEdit && <DagHeader dag={data!} />,
    rows: 25,
  };

  return (
    <Card title={`Airflow DAG: ${data?.title}`}>
      <DataTable data={data?.tasks ?? []} tableConfig={tableConfig} columns={columns} />
    </Card>
  );
};

export default AirflowTasks;

type DagHeaderProps = {
  dag: AirflowDag;
};
const DagHeader: FC<DagHeaderProps> = ({ dag }) => {
  const [createTaskDialog, setCreateTaskDialog] = useState(false);
  const [editDagDialog, setEditDagDialog] = useState(false);

  return (
    <div className="flex justify-content-between px-3 py-2">
      <div className="flex gap-2">
        <Button
          label={T("action.create-task")}
          icon="pi pi-plus"
          className="p-button-sm"
          onClick={() => setCreateTaskDialog(true)}
        />
        <Button
          label={`${T("action.edit")} DAG`}
          icon="pi pi-pencil"
          className="p-button-sm"
          onClick={() => setEditDagDialog(true)}
        />
      </div>
      <div>
        <a href={redirectToAirflowGraphUrl(dag?.dagId)} target="_blank" rel="noreferrer">
          <Button
            label={`Airflow DAG ${T("word.graph")}`}
            type="button"
            icon={<img src={airflowLogo} alt="" className="mr-2" />}
            className="p-button-sm p-button-outlined"
            severity="contrast"
          />
        </a>
      </div>
      <Dialog
        visible={createTaskDialog}
        header="Create new task"
        onHide={() => setCreateTaskDialog(false)}
        style={{ width: "min(100%, 700px)" }}
      >
        <AirflowTaskForm closeDialog={() => setCreateTaskDialog(false)} dag={dag} />
      </Dialog>

      <Dialog
        visible={editDagDialog}
        header="Edit DAG"
        onHide={() => setEditDagDialog(false)}
        style={{ width: "min(100%, 700px)" }}
      >
        <AirflowDagForm closeDialog={() => setEditDagDialog(false)} dag={dag} />
      </Dialog>
    </div>
  );
};